import React, { useMemo, useEffect } from 'react';
import { Grid, Divider } from '@mui/material';
import { RenderedArticle } from '@components/Articles/RenderedArticle';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { articleContentAreaStyles } from '@components/styleObjects/ArticleContentArea';
import { BlogArticle } from '@models/blog';
import { breakpoints, Pill } from '@web-for-marketing/react-ui';
import { TableOfContents } from '@components/TableOfContents/TableOfContentsV2';
import { BlogSubscriptionBox } from '../Shared/BlogSubscriptionBox';
import { AuthorCardV2 } from '../Author/AuthorCardV2';
import { useAtom, useSetAtom } from 'jotai';
import { navHeightAtom, selectedLanguageAtom } from '@atoms/appSettings';
import { KeyTakeaways } from '@components/BlogSection/BlogComponents/BlogPage/KeyTakeaways';
import { getTranslation } from '@helpers/languageTranslations';
import { useTranslation } from '@stateManagement/TranslationContext';
import { isProgressBarVisibleAtom } from '@atoms/navigation';
import { getLanguageByCode, getLanguageRouteAndPrefixByCodeAndRouteKey } from '@helpers/languages';
import { ArticleFAQAccordionList } from '../Shared/ArticleFAQAccordionList';
import { ShareSocialMediaButtons } from '@components/Articles/ShareSocialMediaButtons';
import { ensureInternalURLTrailingSlash } from '@helpers/string';
import { languageRouteTypes } from '@helpers/languageRoutes';

const classes = {
    ...articleContentAreaStyles,
    divider: {
        width: '100%',
    },
    mainContainer: {
        margin: '0',
        padding: '5.6rem 0',
        position: 'relative',
        background: '#FFFFFF',
        [`@media (max-width: ${breakpoints.lg})`]: {
            padding: '5.6rem 0',
        },
        [`@media (max-width: ${breakpoints.md})`]: {
            padding: '0',
        },
    },
    containerToC: (navHeight: number) =>
        (({
            position: 'sticky',
            top: navHeight,
            float: 'right',
        }) as const),
    shareAndLikeIcons: {
        display: 'flex',
    },

    mobileSubscribeContainer: {
        display: 'none',
        [`@media (max-width: ${breakpoints.md})`]: {
            display: 'block',
        },
    },
    tableOfContentContainer: {
        display: 'block',
        [`@media (max-width: ${breakpoints.md})`]: {
            display: 'none',
        },
    },
} as const;

function addButtonClass(node: HTMLDivElement | null): void {
    if (node) {
        const buttonLinks = node.querySelectorAll('a[data-button="true"]');
        buttonLinks.forEach((button) => button.classList.add('blog_button'));
    }
}

interface BlogContentAreaProps {
    blogPost: BlogArticle;
}
export function BlogContentArea({ blogPost }: BlogContentAreaProps): React.ReactElement {
    const { mediaUrlPrefix } = useStaticValues();
    const [navHeight] = useAtom(navHeightAtom);
    const { selectedLanguageTranslation } = useTranslation();
    const [selectedLanguage] = useAtom(selectedLanguageAtom);
    const setIsProgressBarVisible = useSetAtom(isProgressBarVisibleAtom);
    const showGeotabSubscribe = getLanguageByCode(selectedLanguage)?.showGeotabSubscribe;
    const visibleTags = blogPost.tags?.filter(tag => !tag.hidden);

    useEffect(() => {
        setIsProgressBarVisible(true);
    }, [setIsProgressBarVisible]);

    const blogFAQTitleTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'articleFAQ', 'title'),
        [selectedLanguageTranslation]
    );

    const hasHeadermedia = blogPost.imageUrl || blogPost.mobileImageUrl;

    return (
        <Grid
            component='section'
            container
            css={[classes.mainContainer, hasHeadermedia ? { margin: 0 } : undefined]}
            spacing={{ xs: 0, md: 7, lg: 8 }}
        >
            <Grid
                container
                ref={addButtonClass}
                flexDirection='column'
                size={{ xs: 12, md: 8 }}
                rowSpacing={{ xs: 4, md: 6 }}
            >
                {blogPost.keyTakeaways ? (
                    <div>
                        <KeyTakeaways content={blogPost.keyTakeaways} />
                    </div>
                ) : null}
                <RenderedArticle content={blogPost.htmlContent} mediaUrlPrefix={mediaUrlPrefix} />
                {showGeotabSubscribe ? (
                    <div css={classes.mobileSubscribeContainer}>
                        <BlogSubscriptionBox type='short' />
                    </div>
                ) : null}
                {blogPost.frequentlyAskedQuestions?.length ? (
                    <ArticleFAQAccordionList
                        lists={blogPost.frequentlyAskedQuestions}
                        title={blogFAQTitleTranslation}
                    />
                ) : null}
                <ShareSocialMediaButtons css={classes.shareAndLikeIcons} />
                {visibleTags.length ? (
                    <Grid container spacing={1.5}>
                        {visibleTags.map((tag) => (
                            <Pill
                                renderLink={
                                    <a
                                        href={ensureInternalURLTrailingSlash(
                                            `${getLanguageRouteAndPrefixByCodeAndRouteKey(selectedLanguage, languageRouteTypes.TAG)}${
                                                tag.slug
                                            }`
                                        )}
                                    >
                                        {tag.name ? tag.name : tag.title}
                                    </a>
                                }
                                key={tag.name || tag.title}
                                variant='primaryFilled'
                                size='medium'
                            />
                        ))}
                    </Grid>
                ) : null}
                <Divider css={classes.divider}></Divider>
                <Grid size={{ xs: 12 }} css={classes.displayContainer}>
                    <AuthorCardV2 author={blogPost.author} isBlogPostVisible={blogPost.visible} />
                </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} flexDirection={{ md: 'column-reverse' }}>
                <div css={[classes.containerToC(navHeight), classes.tableOfContentContainer]}>
                    <Grid container direction='column'>
                        <TableOfContents content={blogPost.htmlContent} id='blogtoc' />
                        {showGeotabSubscribe ? <BlogSubscriptionBox type='short' /> : null}
                    </Grid>
                </div>
            </Grid>
        </Grid>
    );
}
